<template>
    <semester-required
        @semester-loaded="onSemesterLoaded"
    >
        <template #semester-in-progress="{ semester }">
            <b-container>
                <b-card
                    text-variant="white"
                    bg-variant="themed"
                    class="text-center"
                >
                    <h2>
                        <font-awesome-icon
                            icon="file-contract"
                            class="mr-2"
                        ></font-awesome-icon>
                        Membership Form &mdash; {{ semester.label }}
                    </h2>
                </b-card>
                <b-alert class="mt-3" variant="info" :show="previouslySubmitted && !disabled">
                    <font-awesome-icon class="mr-2" icon="info-circle"></font-awesome-icon>
                    {{ membershipInfo.verify_info_message }}
                </b-alert>
                <div v-if="previouslySubmitted && !formData.expired" class="text-center mt-3">
                    <button
                        v-if="pdf_url"
                        @click="downloadPDF()"
                        class="sc-btn sc-btn-lg"
                        download
                    >
                        <font-awesome-icon icon="download"></font-awesome-icon> Download PDF
                    </button>
                </div>
                <b-card
                    class="mt-3"
                >
                    <template #header>
                        <font-awesome-icon
                            icon="mobile-alt"
                            class="mr-2"
                        ></font-awesome-icon>
                        Phone Numbers
                    </template>
                    <b-form-group
                        :label="user.person.first_name + '\'s Cell Phone*'"
                    >
                        <phone-input v-model="formData.cell_phone" :disabled="disabled"></phone-input>
                        <b-form-invalid-feedback :state="formData.cell_phone != null && formData.cell_phone.length > 0">
                            * Required field
                        </b-form-invalid-feedback>
                    </b-form-group>
                    <b-form-group
                        v-if="user.person2"
                        :label="user.person2.first_name + '\'s Cell Phone'"
                    >
                        <phone-input v-model="formData.spouse_cell_phone" :disabled="disabled"></phone-input>
                    </b-form-group>
                    <b-form-group
                        label="Home Phone"
                    >
                        <phone-input v-model="formData.home_phone" :disabled="disabled"></phone-input>
                    </b-form-group>
                </b-card>
                <b-card
                    class="mt-2"
                >
                    <template #header>
                        <font-awesome-icon
                            icon="map-marker-alt"
                            class="mr-2"
                        ></font-awesome-icon>
                        Address
                    </template>
                    <b-row>
                        <b-col sm="7">
                            <b-form-group
                                label="Address"
                            >
                                <b-input placeholder="Address" v-model="formData.addr1_addr" :disabled="disabled"></b-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col sm="7">
                            <b-form-group
                                label="City"
                            >
                                <b-input placeholder="City" v-model="formData.addr1_city" :disabled="disabled"></b-input>
                            </b-form-group>
                        </b-col>
                        <b-col sm="2">
                            <b-form-group
                                label="State"
                            >
                                <b-form-input list="input-list"  placeholder="State" v-model="formData.addr1_state" :disabled="disabled"></b-form-input>
                                <b-form-datalist id="input-list" :options="stateOptions"></b-form-datalist>
                            </b-form-group>
                        </b-col>
                        <b-col sm="2">
                            <b-form-group
                                label="Zip"
                            >
                                <b-input placeholder="Zip" v-model="formData.addr1_zip" :disabled="disabled"></b-input>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
                <b-card
                    class="mt-2"
                >
                    <template #header>
                        <font-awesome-icon
                            icon="church"
                            class="mr-2"
                        ></font-awesome-icon>
                        Church
                    </template>
                    <b-form-group
                        label="Church of Fellowship"
                    >
                        <b-input placeholder="Church name" v-model="formData.church_of_fellowship" :disabled="disabled"></b-input>
                    </b-form-group>
                </b-card>
                <b-card
                    class="mt-2"
                >
                    <template #header>
                        <font-awesome-icon
                            icon="file-contract"
                            class="mr-2"
                        ></font-awesome-icon>
                        {{ membershipInfo.terms_title }}
                    </template>
                    <div
                        v-for="(terms_section, idx) in membershipInfo.terms_sections"
                        :key="`terms-section-${idx}`"
                    >
                        <h5><u>{{ terms_section.section_title }}</u></h5>
                        <p>{{ terms_section.section_content }}</p>
                    </div>
                    <hr/>
                    <div
                        v-for="(terms_link, idx) in membershipInfo.terms_links"
                        :key="`terms-link-${idx}`"
                    >
                        <h5>{{ terms_link.link_title }}</h5>
                        <a :href="terms_link.link_content" target="_blank">
                            {{ terms_link.link_content }}
                        </a>
                    </div>
                </b-card>
                <b-card
                    class="mt-2"
                >
                    <b-checkbox
                        v-model="agree_terms"
                        :state="agree_terms"
                        :disabled="disabled"
                    >
                        {{ membershipInfo.agree_terms_message }}
                    </b-checkbox>
                </b-card>
                <b-card
                    class="mt-2"
                >
                    <template #header>
                        <font-awesome-icon
                            icon="signature"
                            class="mr-2"
                        ></font-awesome-icon>
                        Signature
                    </template>
                    <b-form-group
                        label="Name"
                    >
                        <b-form-input placeholder="Name" v-model="formData.signature1" type="text" :disabled="disabled"></b-form-input>
                    </b-form-group>
                    <b-form-group
                        v-if="user.person2"
                        label="Spouse's Name"
                    >
                        <b-form-input placeholder="Spouse's Name" v-model="formData.signature2" type="text" :disabled="disabled"></b-form-input>
                    </b-form-group>
                </b-card>
                <div class="text-center">
                    <button
                        v-if="!submitted"
                        @click="submitMembershipForm()"
                        :disabled="disabled || !agree_terms"
                        class="sc-btn sc-btn-lg pill mt-4"
                    >
                        <b-spinner
                            v-if="submitting"
                            variant="light"
                            class="mr-2"
                        ></b-spinner>
                        <font-awesome-icon v-else class="mr-2" :icon="(submitted) ? 'check' : 'file'"></font-awesome-icon>
                        Submit Membership Form
                    </button>
                    <router-link
                        v-else
                        :to="{ name: 'register' }"
                        class="sc-btn sc-btn-lg pill mt-4"
                    >
                        Next: Register Students <font-awesome-icon icon="chevron-right"></font-awesome-icon>
                    </router-link>
                </div>
            </b-container>
        </template>
    </semester-required>
</template>
<script>
import PhoneInput from "@/components/form/PhoneInput.vue";
import { mapState } from 'vuex';
import SemesterRequired from "@/components/authz/SemesterRequired.vue";

export default {
    components: { PhoneInput, SemesterRequired },
    data() {
        return {
            loading                     : false,
            submitting                  : false,
            submitted                   : false,
            previouslySubmitted         : false,
            disabled                    : true,
            agree_terms                 : false,
            pdf_url                     : "",
            formData: {
                addr1_addr              : "",
                addr1_city              : "",
                addr1_state             : "",
                addr1_zip               : "",
                cell_phone              : "",
                spouse_cell_phone       : "",
                home_phone              : "",
                church_of_fellowship    : "",
                signature1              : "",
                signature2              : "",
                user_id                 : null,
                semester_id             : null
            },
            stateOptions: [ "Ohio", "OH" ],
            membershipInfo: {}
        };
    },
    mounted() {
        this.onSemesterLoaded();
    },
    methods: {
        onSemesterLoaded() {
            if (typeof this.semester.id == "number") {
                this.formData.user_id     = this.user.id;
                this.formData.semester_id = this.semester.id;
                this.loadPrefillData();
            } else {
                this.disabled = true;
            }
        },
        loadPrefillData() {
            this.loading = true;
            this.$api.get(
                "/api/membership/prefill"
            ).then(({ data: { prefillData, membershipInfo, pdf_url, success, message } }) => {
                let prefill = {};
                try {
                    this.membershipInfo = JSON.parse(membershipInfo.form);
                    prefill.mf_version_id = membershipInfo.mf_version_id;
                } catch (error) {
                    console.error("Error occurred while processing membership info:", error);
                }
                if (prefillData && typeof prefillData.id == "number") {
                    this.previouslySubmitted = true;
                    this.formData            = { ...prefillData, ...prefill, };
                    this.pdf_url             = pdf_url;

                    if (this.formData.expired) {
                        this.formData.user_id     = this.user.id;
                        this.formData.semester_id = this.semester.id;
                        this.formData.signature1  = "";
                        this.formData.signature2  = "";
                        this.disabled = false;
                    } else {
                        this.disabled             = true;
                        this.agree_terms          = true;
                    }
                } else {
                    // TODO: No prefill data case
                    this.disabled = false;
                }
                this.loading = false;
            });
        },
        submitMembershipForm() {
            this.submitting = true;
            this.disabled   = true;
            this.$api.post(
                "/api/membership/save-form",
                this.formData
            ).then((response) => {
                const data = response.data;
                if (data.submitted == true) {
                    this.submitted  = true;
                } else {
                    this.disabled = false;
                }
                this.$bvToast.toast(data.message, {
                    title:   (data.success) ? "Success" : "Error",
                    variant: (data.success) ? "success" : "danger",
                    autoHideDelay: 5000,
                    appendToast: true
                });
                this.submitting = false;
            });
        },
        downloadPDF() {
            this.$api.get(
                this.pdf_url,
                {
                    responseType: "blob"
                },
            ).then(({ data }) => {
                const downloadUrl = window.URL.createObjectURL(new Blob([data]));
                const link = document.createElement("a");
                link.href = downloadUrl;
                link.setAttribute("download", "MembershipForm - " + this.user.person.last_name + ".pdf");
                document.body.appendChild(link);
                link.click();
                link.remove();
            });
        }
    },
    computed: mapState(["semester", "user"]),
    watch: {
        semester() {
            this.onSemesterLoaded();
        }
    }
}
</script>
<style>
    .multi-steps > li.is-active:before, .multi-steps > li.is-active ~ li:before {
        content: counter(stepNum);
        font-family: inherit;
        font-weight: 700;
    }
    .multi-steps > li.is-active:after, .multi-steps > li.is-active ~ li:after {
        background-color: #ededed;
    }
    .multi-steps {
        display: table;
        table-layout: fixed;
        width: 100%;
    }
    .multi-steps > li {
        counter-increment: stepNum;
        text-align: center;
        display: table-cell;
        position: relative;
        color: #5cb85c;
    }
    .multi-steps > li:before {
        content: '\f00c';
        content: '\2713;';
        content: '\10003';
        content: '\10004';
        content: '\2713';
        display: block;
        margin: 0 auto 4px;
        background-color: #fff;
        width: 36px;
        height: 36px;
        line-height: 32px;
        text-align: center;
        font-weight: bold;
        border-width: 2px;
        border-style: solid;
        border-color: #5cb85c;
        border-radius: 50%;
    }
    .multi-steps > li:after {
        content: '';
        height: 2px;
        width: 100%;
        background-color: #5cb85c;
        position: absolute;
        top: 16px;
        left: 50%;
        z-index: -1;
    }
    .multi-steps > li:last-child:after {
        display: none;
    }
    .multi-steps > li.is-active:before {
        background-color: #fff;
        border-color: #5cb85c;
    }
    .multi-steps > li.is-active ~ li {
        color: #808080;
    }
    .multi-steps > li.is-active ~ li:before {
        background-color: #ededed;
        border-color: #ededed;
    }
</style>